import { stripHtml } from '@worldcoo/shared/utils';
import { useEffect } from 'react';

/**
 * Sets SEO meta tags via both React elements (for SSR) and DOM API (for client).
 *
 * React 19 Float hoists <meta> elements to <head> during SSR, which is correct.
 * However, during client-side hydration, Float removes these tags when Suspense
 * boundaries replay — even if the data is available. The inline `useEffect`
 * re-applies the tags after hydration so they survive in the final DOM.
 */
export const Seo = (props: { title?: string; description?: string; image?: string }) => {
	const description = props.description ? stripHtml(props.description) : undefined;

	// Re-apply meta tags after hydration to survive React 19 Float cleanup
	useEffect(() => {
		if (props.title) document.title = props.title;
		setMeta('description', description);
		setMeta('twitter:title', props.title);
		setMeta('twitter:description', description);
		setMeta('twitter:creator', '@worldcoo');
		setMeta('twitter:site', '@worldcoo');
		setMeta('og:type', 'website');
		setMeta('og:title', props.title);
		setMeta('og:description', description);
		if (props.image) {
			setMeta('twitter:image', props.image);
			setMeta('twitter:card', 'summary_large_image');
			setMeta('og:image', props.image);
		}
	}, [props.title, description, props.image]);

	// SSR: React elements are hoisted to <head> by Float (for non-JS bots/crawlers)
	return (
		<>
			{props.title && <title>{props.title}</title>}
			{description && <meta name="description" content={description} />}
			<meta name="twitter:title" content={props.title} />
			{description && <meta name="twitter:description" content={description} />}
			<meta name="twitter:creator" content="@worldcoo" />
			<meta name="twitter:site" content="@worldcoo" />
			<meta name="og:type" content="website" />
			<meta name="og:title" content={props.title} />
			{description && <meta name="og:description" content={description} />}
			{props.image && (
				<>
					<meta name="twitter:image" content={props.image} />
					<meta name="twitter:card" content="summary_large_image" />
					<meta name="og:image" content={props.image} />
				</>
			)}
		</>
	);
};

function setMeta(name: string, content: string | undefined) {
	if (!content) return;
	let el = document.head.querySelector(`meta[name="${name}"]`);
	if (!el) {
		el = document.createElement('meta');
		el.setAttribute('name', name);
		document.head.appendChild(el);
	}
	el.setAttribute('content', content);
}
